import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getPosts, deletePost, updatePostStatus } from '../../shared/api/posts';
import { Post } from '../../shared/types/post';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminLoginPath, getAdminPostsPath } from '../../shared/config/env';
import AdminSidebar from '../components/AdminSidebar';
import './PostList.css';

function PostList() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);
  const [updatingPostId, setUpdatingPostId] = useState<string | null>(null);
  const [selectedPostIds, setSelectedPostIds] = useState<Set<string>>(new Set());
  const [isDeletingMultiple, setIsDeletingMultiple] = useState<boolean>(false);
  const [isUpdatingMultiple, setIsUpdatingMultiple] = useState<boolean>(false);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [hasPrev, setHasPrev] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const adminLoginPath = getAdminLoginPath();
  const adminPostsPath = getAdminPostsPath();

  // URLクエリパラメータからpageを取得
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // URLクエリパラメータからpost_idsを取得
  const postIdsFilter = useMemo(() => {
    const postIdsParam = searchParams.get('post_ids');
    if (!postIdsParam) {
      return null;
    }
    // カンマ区切りのpost_idsを配列に変換
    return postIdsParam.split(',').map(id => id.trim()).filter(id => id.length > 0);
  }, [searchParams]);

  // URLクエリパラメータからtagを取得
  const selectedTag = searchParams.get('tag');
  
  // URLクエリパラメータからcategoryを取得
  const selectedCategory = searchParams.get('category');

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      // post_idsフィルターがある場合はサーバー側でフィルタリング
      // ない場合は全ての記事を取得（statusパラメータを指定しない）
      const params: Parameters<typeof getPosts>[0] = { 
        page: currentPage,
        per_page: 20,
        skipAuth: false  // 管理画面では認証トークンを送信して、CloudFrontのキャッシュキーを分離する
      };
      if (postIdsFilter && postIdsFilter.length > 0) {
        params.post_ids = postIdsFilter;
      }
      if (selectedTag) {
        params.tag = selectedTag;
      }
      if (selectedCategory) {
        params.category = selectedCategory;
      }
      const response = await getPosts(params);
      setPosts(response.posts);
      setTotal(response.meta.total);
      
      // total_pagesが返されない場合は、totalとper_pageから計算
      const calculatedTotalPages = response.meta.total_pages 
        || (response.meta.total !== undefined 
          ? Math.ceil(response.meta.total / response.meta.per_page)
          : 1);
      setTotalPages(calculatedTotalPages);
      
      // hasNextとhasPrevを計算
      // 優先順位: 1. totalから計算, 2. APIが返したhas_next/has_prev, 3. 取得した記事数から判断
      let calculatedHasNext: boolean;
      let calculatedHasPrev: boolean;
      
      if (response.meta.total !== undefined && response.meta.total > 0) {
        // totalが存在する場合は、それから正確に計算
        calculatedHasNext = currentPage * response.meta.per_page < response.meta.total;
        calculatedHasPrev = currentPage > 1;
      } else {
        // totalがない場合は、取得した記事数から判断
        // 取得した記事数がper_pageと同じ場合は、次のページが存在する可能性が高い
        calculatedHasNext = response.posts.length === response.meta.per_page;
        calculatedHasPrev = currentPage > 1;
        
        // APIがhas_next/has_prevを返している場合は、それも考慮
        // ただし、取得した記事数がper_pageと同じ場合は、hasNextをtrueにする
        if (response.meta.has_next !== undefined) {
          calculatedHasNext = response.meta.has_next || calculatedHasNext;
        }
        if (response.meta.has_prev !== undefined) {
          calculatedHasPrev = response.meta.has_prev || calculatedHasPrev;
        }
      }
      
      setHasNext(calculatedHasNext);
      setHasPrev(calculatedHasPrev);
      // 一覧更新時に選択をクリア
      setSelectedPostIds(new Set());
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('記事の取得に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [postIdsFilter, selectedTag, selectedCategory, currentPage]); // postIdsFilter、selectedTag、selectedCategory、またはcurrentPageが変更されたら再取得

  // 1ページ目は下書きを先頭（更新日降順）、続いて公開記事を公開日降順。2ページ目以降は公開記事を先に表示。
  const filteredPosts = useMemo(() => {
    const draftsFirst = currentPage === 1;
    return [...posts].sort((a, b) => {
      const aHasPublishedAt = !!a.published_at;
      const bHasPublishedAt = !!b.published_at;
      if (aHasPublishedAt && bHasPublishedAt) {
        return new Date(b.published_at!).getTime() - new Date(a.published_at!).getTime();
      }
      if (!aHasPublishedAt && !bHasPublishedAt) {
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
      if (draftsFirst) {
        if (aHasPublishedAt && !bHasPublishedAt) return 1;
        if (!aHasPublishedAt && bHasPublishedAt) return -1;
      } else {
        if (aHasPublishedAt && !bHasPublishedAt) return -1;
        if (!aHasPublishedAt && bHasPublishedAt) return 1;
      }
      return 0;
    });
  }, [posts, currentPage]);

  // ページ変更ハンドラー
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    if (newPage === 1) {
      params.delete('page');
    } else {
      params.set('page', newPage.toString());
    }
    setSearchParams(params, { replace: true });
    // ページトップにスクロール
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStatusToggle = async (post: Post) => {
    // published と draft のみ切り替え可能
    if (post.status !== 'published' && post.status !== 'draft') {
      return;
    }

    // 新しいステータスを決定
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    const newStatusLabel = newStatus === 'published' ? '公開中' : '下書き';

    // 確認ダイアログを表示
    const confirmed = window.confirm(
      `「${post.title}」のステータスを「${newStatusLabel}」に変更してもよろしいですか？`
    );

    if (!confirmed) {
      return;
    }

    try {
      setUpdatingPostId(post.post_id);
      await updatePostStatus(post.post_id, newStatus);
      // 更新成功後、一覧を更新
      await fetchPosts();
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        alert(`ステータスの更新に失敗しました: ${err.message}`);
      } else {
        alert('ステータスの更新に失敗しました');
      }
    } finally {
      setUpdatingPostId(null);
    }
  };

  const handleDelete = async (post: Post) => {
    // 確認ダイアログを表示
    const confirmed = window.confirm(
      `「${post.title}」を削除してもよろしいですか？\n\nこの操作は取り消せません。`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingPostId(post.post_id);
      await deletePost(post.post_id);
      // 削除成功後、一覧を更新
      await fetchPosts();
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        alert(`削除に失敗しました: ${err.message}`);
      } else {
        alert('削除に失敗しました');
      }
    } finally {
      setDeletingPostId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    return `${year}年${month}月${day}日 ${hour}:${minute}`;
  };

  const handleCopyPostId = async (postId: string) => {
    const linkTag = `[](post:${postId})`;
    try {
      await navigator.clipboard.writeText(linkTag);
      setToastMessage('✓ 記事リンクタグをクリップボードにコピーしました');
      setTimeout(() => setToastMessage(null), 3000);
    } catch (err) {
      setToastMessage('✗ 記事リンクタグのコピーに失敗しました');
      setTimeout(() => setToastMessage(null), 3000);
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'published':
        return 'post-list__status-badge post-list__status-badge--published';
      case 'draft':
        return 'post-list__status-badge post-list__status-badge--draft';
      case 'archived':
        return 'post-list__status-badge post-list__status-badge--archived';
      default:
        return 'post-list__status-badge';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'published':
        return '公開中';
      case 'draft':
        return '下書き';
      case 'archived':
        return 'アーカイブ';
      default:
        return status;
    }
  };

  const handleToggleSelect = (postId: string) => {
    setSelectedPostIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const allSelectableIds = filteredPosts.map((post) => post.post_id);
    
    if (allSelectableIds.every((id) => selectedPostIds.has(id))) {
      // すべて選択されている場合は、すべて解除
      setSelectedPostIds(new Set());
    } else {
      // すべて選択
      setSelectedPostIds(new Set(allSelectableIds));
    }
  };

  const handleUpdateStatusMultiple = async (newStatus: 'draft' | 'published') => {
    if (selectedPostIds.size === 0) {
      return;
    }

    const selectedPosts = filteredPosts.filter((post) => selectedPostIds.has(post.post_id));
    const statusLabel = newStatus === 'published' ? '公開中' : '下書き';
    const titles = selectedPosts.map((post) => post.title).join('、');
    
    if (!window.confirm(`以下の${selectedPostIds.size}件の記事のステータスを「${statusLabel}」に変更してもよろしいですか？\n\n${titles}`)) {
      return;
    }

    try {
      setIsUpdatingMultiple(true);
      const updatePromises = Array.from(selectedPostIds).map((postId) => 
        updatePostStatus(postId, newStatus)
      );
      await Promise.all(updatePromises);
      // 更新成功後、一覧を再取得
      await fetchPosts();
      setSelectedPostIds(new Set());
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        alert(`ステータスの更新に失敗しました: ${err.message}`);
      } else {
        alert('ステータスの更新に失敗しました');
      }
    } finally {
      setIsUpdatingMultiple(false);
    }
  };

  const handleDeleteMultiple = async () => {
    if (selectedPostIds.size === 0) {
      return;
    }

    const selectedPosts = filteredPosts.filter((post) => selectedPostIds.has(post.post_id));
    // 公開中の記事が含まれている場合は削除不可
    const publishedPosts = selectedPosts.filter((post) => post.status === 'published');
    if (publishedPosts.length > 0) {
      const publishedTitles = publishedPosts.map((post) => post.title).join('、');
      alert(`公開中の記事は削除できません。\n\n${publishedTitles}`);
      return;
    }

    const titles = selectedPosts.map((post) => post.title).join('、');
    
    if (!window.confirm(`以下の${selectedPostIds.size}件の記事を削除してもよろしいですか？\n\n${titles}\n\nこの操作は取り消せません。`)) {
      return;
    }

    try {
      setIsDeletingMultiple(true);
      const deletePromises = Array.from(selectedPostIds).map((postId) => deletePost(postId));
      await Promise.all(deletePromises);
      // 削除成功後、一覧を再取得
      await fetchPosts();
      setSelectedPostIds(new Set());
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        alert(`削除に失敗しました: ${err.message}`);
      } else {
        alert('削除に失敗しました');
      }
    } finally {
      setIsDeletingMultiple(false);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-layout__main">
        <div className="post-list">
          <header className="post-list__header">
            <h1 className="post-list__title">
              {postIdsFilter && postIdsFilter.length > 0 
                ? `記事管理（${postIdsFilter.length}件の記事を表示）`
                : selectedTag && total !== undefined
                ? `記事管理（${total}件の記事を表示）`
                : selectedCategory && filteredPosts.length > 0
                ? `記事管理（${filteredPosts.length}件の記事を表示）`
                : '記事管理'}
            </h1>
            <div className="post-list__actions">
              {(postIdsFilter && postIdsFilter.length > 0) || selectedTag || selectedCategory ? (
                <Link 
                  to={adminPostsPath} 
                  className="post-list__button post-list__button--secondary"
                >
                  フィルター解除
                </Link>
              ) : null}
              <Link to={`${adminPostsPath}/new`} className="post-list__button post-list__button--primary">
                新規作成
              </Link>
            </div>
          </header>
          <main className="post-list__content">
        {toastMessage && (
          <div className="post-list-toast">
            {toastMessage}
          </div>
        )}

        {loading ? (
          <p className="post-list__loading">読み込み中...</p>
        ) : error ? (
          <p className="post-list__error">{error}</p>
        ) : filteredPosts.length === 0 ? (
          <p className="post-list__empty">
            {postIdsFilter && postIdsFilter.length > 0 
              ? '該当する記事がありません'
              : '記事がありません'}
          </p>
        ) : (
          <>
            {selectedPostIds.size > 0 && (
              <div className="post-list__bulk-actions">
                <span className="post-list__bulk-actions-info">
                  {selectedPostIds.size}件選択中
                </span>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="post-list__button post-list__button--select-all"
                >
                  すべて選択/解除
                </button>
                <button
                  type="button"
                  onClick={() => handleUpdateStatusMultiple('published')}
                  disabled={isUpdatingMultiple || isDeletingMultiple}
                  className="post-list__button post-list__button--bulk-status"
                >
                  {isUpdatingMultiple ? '更新中...' : `選択した${selectedPostIds.size}件を公開中に変更`}
                </button>
                <button
                  type="button"
                  onClick={() => handleUpdateStatusMultiple('draft')}
                  disabled={isUpdatingMultiple || isDeletingMultiple}
                  className="post-list__button post-list__button--bulk-status"
                >
                  {isUpdatingMultiple ? '更新中...' : `選択した${selectedPostIds.size}件を下書きに変更`}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteMultiple}
                  disabled={
                    isDeletingMultiple || 
                    isUpdatingMultiple ||
                    filteredPosts
                      .filter((post) => selectedPostIds.has(post.post_id))
                      .some((post) => post.status === 'published')
                  }
                  className="post-list__button post-list__button--bulk-delete"
                  title={
                    filteredPosts
                      .filter((post) => selectedPostIds.has(post.post_id))
                      .some((post) => post.status === 'published')
                      ? '公開中の記事は削除できません'
                      : ''
                  }
                >
                  {isDeletingMultiple ? '削除中...' : `選択した${selectedPostIds.size}件を削除`}
                </button>
              </div>
            )}
            <div className="post-list__table-container">
              <table className="post-list__table">
                <thead>
                  <tr>
                    <th className="post-list__th post-list__th--checkbox">
                      <input
                        type="checkbox"
                        checked={
                          filteredPosts.length > 0 &&
                          filteredPosts.every((post) => selectedPostIds.has(post.post_id))
                        }
                        onChange={handleSelectAll}
                        disabled={isDeletingMultiple || isUpdatingMultiple}
                        title="すべて選択/解除"
                      />
                    </th>
                    <th className="post-list__th">タイトル</th>
                    <th className="post-list__th">ステータス</th>
                    <th className="post-list__th">カテゴリー</th>
                    <th className="post-list__th">タグ</th>
                    <th className="post-list__th">公開日</th>
                    <th className="post-list__th">更新日</th>
                    <th className="post-list__th">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map((post) => (
                    <tr key={post.post_id} className="post-list__row">
                      <td className="post-list__td post-list__td--checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPostIds.has(post.post_id)}
                          onChange={() => handleToggleSelect(post.post_id)}
                          disabled={
                            isDeletingMultiple || 
                            isUpdatingMultiple ||
                            deletingPostId === post.post_id ||
                            updatingPostId === post.post_id
                          }
                        />
                      </td>
                      <td className="post-list__td">
                        <Link
                          to={`${adminPostsPath}/${post.slug}/edit`}
                          className="post-list__link"
                        >
                          {post.title}
                        </Link>
                      </td>
                    <td className="post-list__td">
                      {post.status === 'published' || post.status === 'draft' ? (
                        <button
                          className={`${getStatusBadgeClass(post.status)} post-list__status-badge--clickable`}
                          onClick={() => handleStatusToggle(post)}
                          disabled={updatingPostId === post.post_id}
                          title="クリックしてステータスを切り替え"
                        >
                          {updatingPostId === post.post_id ? '更新中...' : getStatusLabel(post.status)}
                        </button>
                      ) : (
                        <span className={getStatusBadgeClass(post.status)}>
                          {getStatusLabel(post.status)}
                        </span>
                      )}
                    </td>
                    <td className="post-list__td">
                      {post.category && post.category.trim() ? (
                        (() => {
                          // category_slugが存在する場合はslugを使用、ない場合はカテゴリー名からslugを生成（後方互換性）
                          const categorySlug = (post as any).category_slug || post.category.toLowerCase().replace(/\s+/g, '-');
                          const isSelected = selectedCategory === categorySlug;
                          return (
                            <button
                              type="button"
                              className={`post-list__category ${isSelected ? 'post-list__category--selected' : 'post-list__category--clickable'}`}
                              onClick={() => {
                                if (isSelected) {
                                  // 既に選択されている場合はフィルター解除
                                  navigate(adminPostsPath);
                                } else {
                                  // カテゴリーでフィルター（ページを1にリセット）
                                  navigate(`${adminPostsPath}?category=${encodeURIComponent(categorySlug)}`);
                                }
                              }}
                              title={isSelected ? 'フィルター解除' : `${post.category}カテゴリーでフィルター`}
                            >
                              {post.category}
                            </button>
                          );
                        })()
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="post-list__td">
                      {post.tags && post.tags.length > 0 ? (
                        <div className="post-list__tags">
                          {post.tags.map((tag, index) => {
                            // tag_slugsが存在する場合はslugを使用、ない場合はタグ名からslugを生成（後方互換性）
                            const tagSlug = post.tag_slugs && post.tag_slugs[index] 
                              ? post.tag_slugs[index] 
                              : tag.toLowerCase().replace(/\s+/g, '-');
                            const isSelected = selectedTag === tagSlug;
                            return (
                              <button
                                key={tag}
                                type="button"
                                className={`post-list__tag ${isSelected ? 'post-list__tag--selected' : 'post-list__tag--clickable'}`}
                                onClick={() => {
                                  if (isSelected) {
                                    // 既に選択されている場合はフィルター解除
                                    navigate(adminPostsPath);
                                  } else {
                                    // タグでフィルター（ページを1にリセット）
                                    navigate(`${adminPostsPath}?tag=${encodeURIComponent(tagSlug)}`);
                                  }
                                }}
                                title={isSelected ? 'フィルター解除' : `${tag}タグでフィルター`}
                              >
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="post-list__td">
                      {post.published_at ? formatDate(post.published_at) : '-'}
                    </td>
                    <td className="post-list__td">
                      {formatDate(post.updated_at)}
                    </td>
                    <td className="post-list__td">
                      <div className="post-list__actions-cell">
                        <button
                          className="post-list__button post-list__button--copy"
                          onClick={() => handleCopyPostId(post.post_id)}
                          title="記事リンクタグをコピー"
                        >
                          コピー
                        </button>
                        <button
                          className="post-list__button post-list__button--delete"
                          onClick={() => handleDelete(post)}
                          disabled={post.status === 'published' || deletingPostId === post.post_id}
                        >
                          {deletingPostId === post.post_id ? '削除中...' : '削除'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                  </tbody>
                </table>
              </div>
              {(totalPages > 1 || hasNext || hasPrev) && (
                <div className="post-list__pagination">
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPrev}
                    className="post-list__pagination-button"
                  >
                    前へ
                  </button>
                  <span className="post-list__pagination-info">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNext}
                    className="post-list__pagination-button"
                  >
                    次へ
                  </button>
                </div>
              )}
            </>
        )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default PostList;

